import { useCallback } from 'react';

import { Condition } from '@/models/ConditionModel.ts';
import { CreatePatientModel } from '@/models/CreatePatientModel.ts';
import { FileModel } from '@/models/FileModel.ts';
import { Patient, PatientID } from '@/models/PatientModel';
import { TeleMonitoringCriteria } from '@/models/TelemonitoringCriteriaModel.ts';
import { Queries } from '@/queries/Queries.ts';
import {
  CarePlanCreationInfoWithPrescription,
  GestationalNoInsulinCarePlanCreationInfo,
} from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreation.ts';
import { CarePlanRenewalInfo } from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanRenewal.ts';
import { PatientCreationInfo } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientCreationInfo.ts';
import { GeneratePrescriptionInfo } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfo.ts';

type GeneratePrescriptionRequest = {
  patientInfo: {
    givenName: string;
    familyName: string;
  };
  carePlan: {
    duration: string;
    description?: string;
    descriptionAttachment?: FileModel;
    inclusionCriteria: TeleMonitoringCriteria;
  };
  prescriptionInfo: {
    birthDate: string;
    weight: number;
  };
};

export const useGeneratePrescription = () => {
  const carePlanDirectives = Queries.practitioner.useCarePlanDirectives({});
  const generatePrescriptionMutation =
    Queries.prescription.useGeneratePrescription();

  return useCallback(
    (
      data: GeneratePrescriptionRequest,
      onSuccess: (data: { prescription: Blob }) => void,
    ) => {
      if (!data.carePlan.inclusionCriteria || !data.carePlan.duration) {
        return;
      }
      generatePrescriptionMutation.mutate(
        {
          first_name: data.patientInfo.givenName,
          last_name: data.patientInfo.familyName,
          birthDate: data.prescriptionInfo.birthDate,
          weight: data.prescriptionInfo.weight,
          telemonitoringTier:
            carePlanDirectives.telemonitoringTierMap[
              data.carePlan.inclusionCriteria
            ],
          duration: parseInt(data.carePlan.duration),
          periodStart:
            carePlanDirectives.carePlanCreationDataDirectives.periodStart,
        },
        {
          onSuccess: prescription => {
            onSuccess({ prescription });
          },
        },
      );
    },
    [
      carePlanDirectives.carePlanCreationDataDirectives.periodStart,
      carePlanDirectives.telemonitoringTierMap,
    ],
  );
};

export const useCreateNoInsulinPatient = (
  onSuccess: (data: PatientID, variables: CreatePatientModel) => void,
) => {
  const createPatientMutation = Queries.practitioner.useCreatePatient();
  return useCallback(
    (
      patientInfo: PatientCreationInfo,
      gestationInfo: GestationalNoInsulinCarePlanCreationInfo,
    ) => {
      createPatientMutation.mutate(
        {
          patient: patientInfo,
          condition: {
            diabetesType: gestationInfo.diabetesType,
            insulinType: gestationInfo.insulinScheme,
            termDate: gestationInfo.termDate,
          },
        },
        { onSuccess },
      );
    },
    [createPatientMutation],
  );
};

export const useCreatePatientAndCarePlan = (
  onSuccess: (data: PatientID, variables: CreatePatientModel) => void,
) => {
  const createPatientMutation = Queries.practitioner.useCreatePatient();

  return useCallback(
    (
      patientInfo: PatientCreationInfo,
      carePlanInfo: CarePlanCreationInfoWithPrescription,
      prescriptionInfo: Partial<GeneratePrescriptionInfo>,
      prescription: FileModel,
    ) => {
      createPatientMutation.mutate(
        {
          patient: {
            birthDate: prescriptionInfo.birthDate,
            weight: prescriptionInfo.weight,
            ...patientInfo,
          },
          care_plan: {
            duration: parseInt(carePlanInfo.duration),
            description: carePlanInfo.description,
            descriptionAttachment: carePlanInfo.descriptionAttachment,
            telemonitoringCriteria: carePlanInfo.inclusionCriteria,
            prescription: prescription,
          },
          condition: {
            diabetesType: carePlanInfo.diabetesType,
            insulinType: carePlanInfo.insulinScheme,
            termDate: carePlanInfo.termDate,
          },
          hsp_id:
            carePlanInfo.insulinScheme === 'insulinPump'
              ? carePlanInfo.homeServiceProviderId
              : undefined,
        },
        { onSuccess },
      );
    },
    [createPatientMutation],
  );
};

export const useRenewPatient = (onSuccess: () => void) => {
  const renewPatientMutation = Queries.practitioner.useRenewCarePlan();
  return useCallback(
    (
      patient: Patient,
      condition: Condition,
      carePlanInfo: CarePlanRenewalInfo,
      prescriptionInfo: Partial<GeneratePrescriptionInfo>,
      prescription: FileModel,
    ) => {
      renewPatientMutation.mutate(
        {
          patientId: patient.id,
          data: {
            diabetesType: carePlanInfo.diabetesType ?? condition.type,
            insulinType:
              carePlanInfo.insulinScheme ?? condition.medicationSchema,
            termDate: carePlanInfo.termDate ?? condition.termDate,
            birthDate: prescriptionInfo?.birthDate ?? patient.birthDate,
            weight: prescriptionInfo?.weight ?? patient.weight,
            duration: parseInt(carePlanInfo.duration),
            description: carePlanInfo.description,
            descriptionAttachment: carePlanInfo.descriptionAttachment,
            telemonitoringCriteria: carePlanInfo.inclusionCriteria,
            prescription: prescription,
          },
        },
        { onSuccess },
      );
    },
    [renewPatientMutation],
  );
};

export const useSignPrescriptionAndCreatePatient = (
  onSuccess: (data: PatientID, variables: CreatePatientModel) => void,
) => {
  const signPrescriptionMutation = Queries.prescription.useSignPrescription();
  const createPatientAndCarePlan = useCreatePatientAndCarePlan(onSuccess);
  const carePlanDirectives = Queries.practitioner.useCarePlanDirectives({});

  return useCallback(
    (
      patientInfo: PatientCreationInfo,
      carePlanInfo: CarePlanCreationInfoWithPrescription,
      prescriptionInfo: GeneratePrescriptionInfo,
    ) => {
      signPrescriptionMutation.mutate(
        {
          first_name: patientInfo.givenName,
          last_name: patientInfo.familyName,
          birthDate: prescriptionInfo.birthDate,
          weight: prescriptionInfo.weight,
          telemonitoringTier:
            carePlanDirectives.telemonitoringTierMap[
              carePlanInfo.inclusionCriteria
            ],
          duration: parseInt(carePlanInfo.duration),
          periodStart:
            carePlanDirectives.carePlanCreationDataDirectives.periodStart,
        },
        {
          onSuccess: prescription =>
            createPatientAndCarePlan(
              patientInfo,
              carePlanInfo,
              prescriptionInfo,
              prescription,
            ),
        },
      );
    },
    [signPrescriptionMutation, carePlanDirectives, createPatientAndCarePlan],
  );
};

export const useSignPrescriptionAndRenewPatient = (onSuccess: () => void) => {
  const signPrescriptionMutation = Queries.prescription.useSignPrescription();
  const renewPatient = useRenewPatient(onSuccess);
  const carePlanDirectives = Queries.practitioner.useCarePlanDirectives({});
  return useCallback(
    (
      patient: Patient,
      condition: Condition,
      carePlanInfo: CarePlanRenewalInfo,
      prescriptionInfo: GeneratePrescriptionInfo,
    ) => {
      signPrescriptionMutation.mutate(
        {
          first_name: patient.givenName,
          last_name: patient.familyName,
          birthDate: prescriptionInfo.birthDate,
          weight: prescriptionInfo.weight,
          telemonitoringTier:
            carePlanDirectives.telemonitoringTierMap[
              carePlanInfo.inclusionCriteria
            ],
          duration: parseInt(carePlanInfo.duration),
          periodStart:
            carePlanDirectives.carePlanCreationDataDirectives.periodStart,
        },
        {
          onSuccess: prescription =>
            renewPatient(
              patient,
              condition,
              carePlanInfo,
              prescriptionInfo,
              prescription,
            ),
        },
      );
    },
    [signPrescriptionMutation, carePlanDirectives, renewPatient],
  );
};
